import { useEffect, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import axios from "axios";
import config from '../../config';
import dayjs from 'dayjs';

import '../../components/dashboard/styles/dashboard.css';
import '../../components/dashboard/styles/Rdashboard.css';

import Loading from "../../components/general/loading";

import PageHeader from "../../components/general/pageHeader.js";
import HomeHeader from "../../components/dashboard/residentComponents/homeHeader.js";
import MobileHeader from "../../components/dashboard/residentComponents/mobileHeader.js";
import MiniCard from "../../components/dashboard/residentComponents/miniCard.js";
import Card from "../../components/dashboard/residentComponents/Card.js";
import useAuthenticate from "../../components/authentication/authenticate";

const RDashboard = () => {
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [selectedUnit, setSelectedUnit] = useState(null);

    const { user, isLoading: isUserLoading } = useAuthenticate();

    const activeAccountId = Object.values(user?.user?.active || {})[0];

    const account = {
        accountId: activeAccountId || null,
        accountName: user?.user?.userData?.givenName ||
            user?.user?.email?.split('@')[0]?.split('.')[0]?.replace(/^./, c => c.toUpperCase())
    };

    const { data: properties, isLoading: isPropertiesLoading } = useQuery({
        queryKey: ['properties'],
        queryFn: fetchProperties,
    });

    const { data: units = [], isLoading: isUnitsLoading } = useQuery({
        queryKey: ['units'],
        queryFn: fetchUnits,
    });

    const { data: invoices, isLoading: isInvoicesLoading } = useQuery({
        queryKey: ['invoices'],
        queryFn: fetchInvoices,
    });

    const { data: creditBalances, isLoading: isCreditBalancesLoading } = useQuery({
        queryKey: ['creditBalances'],
        queryFn: fetchBalances,
    });

    const { data: requests, isLoading: isRequestsLoading } = useQuery({
        queryKey: ['requestsByUser', selectedProperty?.value, activeAccountId],
        queryFn: () => fetchRequestsByRequestedBy(activeAccountId),
        enabled: !!selectedProperty?.value,
    });

    const { data: visitors, isLoading: isVisitorsLoading } = useQuery({
        queryKey: ['visitors', selectedUnit?._id],
        queryFn: () => fetchVisitorsByUnit(selectedUnit?._id),
        enabled: !!selectedUnit,
    });

    const { data: announcements = [], isLoading: isAnnouncementsLoading } = useQuery({
        queryKey: ['announcements', selectedProperty?.value],
        queryFn: () => fetchAnnouncements(selectedProperty.value),
        enabled: !!selectedProperty?.value,
    });

    // Auto select the first property if none is selected
    useEffect(() => {
        if (properties?.length > 0) {
            setSelectedProperty(properties[0]);
        }
    }, [properties, selectedProperty]);

    // Filter units based on how many units users own in the selected property
    const filteredUnits = useMemo(() => {
        return selectedProperty
            ? units.filter(u => u.property?._id === selectedProperty?.value)
            : [];
    }, [units, selectedProperty]);

    // Auto select the first unit if property is selected
    useEffect(() => {
        if (selectedProperty && filteredUnits.length > 0 && !selectedUnit) {
            setSelectedUnit(filteredUnits[0]);
        }
    }, [selectedProperty, filteredUnits, selectedUnit]);

    const { totalBalance, totalInvoiceAmount } = useMemo(() => {
        if (!account.accountId || !creditBalances || !invoices) {
            return { totalBalance: 0, totalInvoiceAmount: 0 };
        }

        const totalBalance = creditBalances
            .filter(b => b.propertyRole?.account === account.accountId)
            .reduce((sum, b) => sum + (b.credit || 0), 0);

        const totalInvoiceAmount = invoices
            .filter(inv => inv.chargedTo?.propertyRole?.account === account.accountId)
            .filter(inv => inv.status !== 'Paid')
            .reduce((sum, inv) => sum + (inv.balance || 0), 0);

        return { totalBalance, totalInvoiceAmount };
    }, [account.accountId, creditBalances, invoices]);

    const sortedPendingRequests = [...(requests || [])]
        .filter(req => req.status === 'Pending')
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const sortedAnnouncements = [...(announcements || [])].sort(
        (a, b) => dayjs(b.createdAt).diff(dayjs(a.createdAt))
    );

    // console.log('Sorted Announcements:', sortedAnnouncements);

    const sortedVisitors = [...(visitors || [])].sort(
        (a, b) => new Date(b.checkIn) - new Date(a.checkIn)
    );

    const isAnyLoading =
        !selectedProperty ||
        isUserLoading ||
        isPropertiesLoading ||
        isUnitsLoading ||
        isInvoicesLoading ||
        isCreditBalancesLoading ||
        isRequestsLoading ||
        isVisitorsLoading ||
        isAnnouncementsLoading;

    if (isAnyLoading) return <Loading />;

    return (
        <div className="w-full">
            <div className="r-dashboard-header">
                <PageHeader pageName="Home" />

                <div className="hidden lg:flex">
                    <HomeHeader
                        userData={user}
                        propertiesData={properties}
                        selectedProperty={selectedProperty}
                        onPropertyChange={setSelectedProperty}
                        units={filteredUnits}
                        selectedUnit={selectedUnit}
                        onUnitChange={setSelectedUnit}
                        isUnitsLoading={isUnitsLoading}
                    />
                </div>

                <div className="lg:hidden">
                    <MobileHeader
                        userData={user}
                        propertiesData={properties}
                        selectedProperty={selectedProperty}
                        onPropertyChange={setSelectedProperty}
                        units={filteredUnits}
                        selectedUnit={selectedUnit}
                        onUnitChange={setSelectedUnit}
                        isUnitsLoading={isUnitsLoading}
                    />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="square-item gap-4">
                        <div className="flex flex-row gap-4">
                            <MiniCard label="Balance" value={<> PHP <br /> {formatMoney(totalBalance)}</>} />
                            <MiniCard label="Invoice Amount" value={<> PHP <br /> {formatMoney(totalInvoiceAmount)}</>} />
                        </div>
                        <Card
                            label="Pending Requests"
                            labelIcon="fa fa-clipboard-list"
                            background="#818FB4"
                            data={sortedPendingRequests}
                            redirectTo={`/requests/requests`}
                            columns={[
                                { label: 'Type', accessor: 'type', width: '15%' },
                                { label: 'Status', accessor: 'status', width: '15%' },
                                {
                                    label: 'Description',
                                    accessor: (row) => {
                                        const line = row.description?.split('\n')[0]; // Only show the "Request" part
                                        const [label, value] = line?.split(':').map(s => s.trim()) || [];
                                        return <span><strong>{label}:</strong> {value}</span>;
                                    },
                                    width: '40%',
                                },
                                { label: 'Requested On', accessor: row => new Date(row.createdAt).toLocaleDateString(), width: '15%' },
                            ]}
                        />
                    </div>
                    <div className="square-item gap-4">
                        <Card
                            label="Announcements"
                            labelIcon="fa fa-bullhorn"
                            background="#C86B2C"
                            data={sortedAnnouncements}
                            redirectTo={`/announcements`}
                            columns={[
                                { label: 'Title', accessor: 'announcementTitle', width: '50%' },
                                { label: 'Date', accessor: row => dayjs(row.createdAt).format('DD/MM/YYYY'), width: '20%' },
                            ]}
                        />
                        <Card
                            label="Visitors"
                            labelIcon="fa fa-users"
                            loading={isVisitorsLoading}
                            background="#C86B2C"
                            data={sortedVisitors}
                            redirectTo={`/visitors`}
                            columns={[
                                { label: 'Name', accessor: (row) => `${row.givenName || ''} ${row.lastName || ''}`.trim(), width: '30%' },
                                { label: 'Unit', accessor: row => row.unit?.unitNumber || 'N/A', width: '15%' },
                                { label: 'Purpose', accessor: 'purpose', width: '25%' },
                                { label: 'Check-In', accessor: (row) => new Date(row.checkIn).toLocaleDateString(), width: '25%' },
                            ]}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const fetchProperties = async () => {
    const response = await axios.get(`${config.API_URL}/v2/property`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
    console.log("Properties: ", response.data)
    return response.data.map(item => ({
        value: item._id,
        label: item.name,
        images: item.images || [],
    }));
}

const fetchUnits = async () => {
    const response = await axios.get(`${config.API_URL}/v2/unit`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    });
    return response.data;
};

const fetchAnnouncements = async (propertyIds) => {
    const response = await axios.get(
        `${config.API_URL}/v2/announcement?property=${propertyIds}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
    );
    return response.data.results;
};

const fetchVisitorsByUnit = async (unitId) => {
    if (!unitId) return [];
    const response = await axios.get(`${config.API_URL}/v2/visitor/unit/${unitId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    });
    console.log("Visitors: ", response.data);
    return response.data;
};

const fetchRequestsByRequestedBy = async (accountId) => {
    const response = await axios.get(
        `${config.API_URL}/v2/request?requestedBy=${accountId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
    );
    console.log('Fetched requests:', response.data);
    return response.data.requests || [];
};

const fetchInvoices = async () => {
    const response = await axios.get(`${config.API_URL}/v2/invoice`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
    return response.data;
};

const fetchBalances = async () => {
    const response = await axios.get(`${config.API_URL}/v2/accountBalance/balance`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
    console.log('Fetched balances:', response.data.balances);
    return response.data.balances;
};

function formatMoney(amount) {
    if (amount == null) {  // Checks for both null and undefined
        return '-';
    }
    return amount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

export default RDashboard;